<template>
  <div class="w-chart">
    <div class="w-toolbar">
      <div class="w-chips">
        <span class="chip"><b>{{ compact(last ? last.co2 : 0) }}</b> t CO2 stored</span>
        <span class="chip" v-if="growth !== null" :class="growth >= 0 ? 'up' : 'down'">
          <i :class="growth >= 0 ? 'fas fa-arrow-up' : 'fas fa-arrow-down'"></i> {{ compact(Math.abs(growth)) }} t in 12 months
        </span>
        <span class="chip" v-if="last"><b>{{ fmt(last.forests) }}</b> forests measured</span>
      </div>
      <div class="w-seg">
        <button v-for="r in ranges" :key="r.key" type="button" :class="{ on: range === r.key }" @click="range = r.key">{{ r.label }}</button>
      </div>
    </div>
    <div class="w-plot">
      <apexchart :key="chartKey" v-if="rows.length" type="line" height="100%" :options="options" :series="series"/>
      <div v-else class="w-empty"><i class="fas fa-seedling"></i>{{ stats.insights ? 'No scans yet' : 'Loading…' }}</div>
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';
import { apexBase, compact, fmt, monthLabel, palette, themeState } from '../theme';

export default {
  name: 'Co2Growth',
  components: { apexchart: VueApexCharts },
  props: { stats: { type: Object, required: true } },
  data() {
    return { range: 'all', ranges: [{ key: '12m', label: '12M' }, { key: '3y', label: '3Y' }, { key: 'all', label: 'All' }] };
  },
  computed: {
    // a theme or data change re-creates the chart: ApexCharts does not always redraw mixed charts on update
    chartKey() { return `${themeState.tick}:${JSON.stringify([this.range, this.series])}`; },
    all() {
      const i = this.stats.insights || {};
      const scans = new Map((i.timeline || []).map((m) => [m.month, m.scans]));
      return (i.stored || []).map((m) => ({ ...m, scans: scans.get(m.month) || 0 }));
    },
    rows() {
      const n = { '12m': 12, '3y': 36 }[this.range];
      return n ? this.all.slice(-n) : this.all;
    },
    last() { return this.all.length ? this.all[this.all.length - 1] : null; },
    growth() {
      const a = this.all;
      return a.length >= 13 ? a[a.length - 1].co2 - a[a.length - 13].co2 : null;
    },
    series() {
      return [
        { name: 'CO2 stored (t)', type: 'area', data: this.rows.map((r) => Math.round(r.co2)) },
        { name: 'Scans that month', type: 'column', data: this.rows.map((r) => r.scans) },
      ];
    },
    options() {
      void themeState.tick;
      const p = palette();
      return apexBase({
        colors: [p.teal, p.green],
        labels: this.rows.map((r) => monthLabel(r.month)),
        stroke: { width: [3, 0], curve: 'smooth' },
        fill: { type: ['gradient', 'solid'], opacity: [1, 0.5], gradient: { shadeIntensity: 1, opacityFrom: 0.55, opacityTo: 0.05, stops: [0, 100] } },
        plotOptions: { bar: { columnWidth: '60%', borderRadius: 3 } },
        markers: { size: 0, hover: { size: 5 } },
        xaxis: { type: 'category', tickAmount: Math.min(10, this.rows.length), labels: { rotate: 0, hideOverlappingLabels: true } },
        yaxis: [
          { seriesName: 'CO2 stored (t)', min: 0, title: { text: 't CO2 stored' }, labels: { formatter: (v) => compact(v) } },
          { seriesName: 'Scans that month', opposite: true, min: 0, title: { text: 'scans' }, labels: { formatter: (v) => compact(v) } },
        ],
        legend: { position: 'top', horizontalAlign: 'right' },
        tooltip: { shared: true, intersect: false, y: { formatter: (v) => fmt(v) } },
      });
    }
  },
  methods: { compact, fmt }
};
</script>

<style scoped lang="scss">
@import '../widgets.scss';
</style>
